import { UnitConverter } from '@vibialighting/vibia-utils';
import { articleAttribute, crmAttribute, i18nLabelInterface, variationType } from './catalog';
import { VariationProps } from './variation';
import { accessoryProductTypology } from '@domain/data/Constants';
import Toggles from '@domain/toggles/Toggles';

const crmKeys = {
	color: 'finish',
	temperature: 'ledColourTemp',
	control: 'control',
	installation: 'installation',
	pendantHeight: 'pendantHeight'
}
export default class Article {
	props: any;

	constructor(articleData) {
		this.props = articleData
		if (this.props && !this.props?.referenceBase) {
			this.props.referenceBase = this.props.reference
		}
		if (this.props.attributes) {
			for (const key of Object.keys(this.props.attributes)) {
				const attr = this.props.attributes[key]
				if (attr && !attr.bov) {
					attr.bov = 'B'
				}
			}
		}
	}

	getProps() {
		return this.props
	}

	isAccessory() {
		return this.getProps().type === "Complement" || this.getProps().articleType?.level1?.id === accessoryProductTypology.level1.id
	}

	isPersonalized() {
		return this.props.reference != this.props.model
	}

	getModel() {
		return this.props?.model
	}

	getReference(priceReference?: boolean) {
		if (Toggles.isEnabled("AvoidMVariationPrices") && priceReference && this.props?.priceReference) {
			return this.props?.priceReference
		}
		return this.props?.reference
	}

	getCatalog() {
		return this.props.catalog?.id
	}

	getStandardPendantHeight() {
		let result = null
		const standardPendant = this.props.attributes?.pendantHeight?.standard?.value
		if (standardPendant) {
			result = standardPendant / 100
		}
		return result
	}

	/* Han de dejar de ser descripciones y tener también el modificador */
	/* FIXME en futuro -> En lugar de sacarlas de ti misma, que las tenga la article collection (Acoplamiento de Variation en Article) */
	/* Estos datos accedidos así, no están al dia, son en el momento que se crearon */
	getAttribute(attribute: articleAttribute, withModifier: boolean = true) {

		switch (attribute) {
			case 'model':
				return this.getModel()

			/* @ts-ignore */
			case 'color':
				withModifier = true // De momento el color siempre lo estamos devolviendo con el modificador delante
			case 'temperature':
			case 'control':
			case 'installation':
				const modifier = this.getAttributeModifier(attribute)
				const attributeKey = Article.getAttributeKey(attribute)
				const attributeValue = this.getProps().attributes[attributeKey]
				if (!attributeValue) return undefined
				return withModifier
					? modifier + ' ' + attributeValue.description.en
					: attributeValue.description.en

			case 'material':
				return this.getProps().materials.map(material => material?.description?.en)

			case 'cri':
				return this.getProps().cri?.description.en

			case 'pendantHeight':
				const pendantHeightValue = this.getProps().attributes?.pendantHeight?.value
				return pendantHeightValue && pendantHeightValue / 100

			default:
				return null
		}
	}

	getAttributeModifier(attribute: variationType) {
		let modifier
		switch (attribute) {
			case 'color':
				modifier = this.getProps().attributes?.finish?.code?.trim()
				break;
			case 'temperature':
				modifier = this.getProps().attributes?.ledColourTemp?.code?.trim()
				break;
			case 'control':
				modifier = this.getProps().attributes?.control?.code?.trim()
				break;
			case 'installation':
				modifier = this.getProps().attributes?.installation?.code?.trim()
				break;
			case 'pendantHeight':
				modifier = this.getProps().attributes?.pendantHeight?.code?.trim()
				break;
		}
		return modifier || '00' // No code in CRM -> '00' in domain
	}

	getAttributeBOV(attribute: variationType) {
		let bov
		switch (attribute) {
			case 'color':
				bov = this.getProps().attributes.finish?.bov
				break;
			case 'temperature':
				bov = this.getProps().attributes.ledColourTemp?.bov
				break;
			case 'control':
				bov = this.getProps().attributes.control?.bov
				break;
			case 'installation':
				bov = this.getProps().attributes.installation?.bov
				break;
			case 'pendantHeight':
				bov = this.getProps().attributes.pendantHeight?.bov
				break;
		}
		return bov || 'B'
	}

	// Will be deprecated when new masters are in place
	getAttributeId(attribute: variationType): number {
		const attributeKey = Article.getAttributeKey(attribute)
		return this.getProps().attributes[attributeKey]?.id
	}

	getAttributeDescription(attribute: articleAttribute): i18nLabelInterface {
		const attributeKey = Article.getAttributeKey(attribute as variationType)
		return this.getProps().attributes[attributeKey]?.description ?? {} as i18nLabelInterface
	}

	setAttributeDescription(attribute: articleAttribute, description: i18nLabelInterface): this {
		if (this.props.attributes?.[crmKeys[attribute]])
			this.props.attributes[crmKeys[attribute]].description = description
		return this
	}

	getMaterialsDescriptions(): i18nLabelInterface[] {
		return this.getProps().materials.map(material => material?.description)
	}

	getCRIDescriptions(): i18nLabelInterface {
		return this.getProps().cri?.description as i18nLabelInterface
	}

	getRal(): VariationProps['ral'] {
		return this.getProps().attributes.finish?.ral ?? {}
	}

	setRal(newRal) {
		this.props.attributes.finish.ral = newRal
		return this
	}

	setFinishThumbnail(newThumbnail: string) {
		this.props.attributes.finish.thumbnail = newThumbnail
		return this
	}


	modifyFinishData(finishesObject) {
		if (!finishesObject) return this
		const finishData = finishesObject[this.getAttributeModifier('color')]

		if (finishData) {
			const modifier = this.getAttributeModifier('color')
			const description = finishData.description

			Object.entries(description).forEach(([lang, desc]: [string, string]) => description[lang] = desc.replace(modifier, '').trim())
			this.setAttributeDescription('color', description)

			const mastersRal = finishData.ral
			const ral = this.getRal()
			Object.keys(ral.description).forEach((lang) => ral.description[lang] = mastersRal)
			this.setRal(ral)
			const thumbnailFinish = finishData.thumbnailImage
			this.setFinishThumbnail(thumbnailFinish)
		}
		return this
	}


	modifyControlData(controlsObject) {
		if (!controlsObject) return this
		const controlData = controlsObject[this.getAttributeModifier('control')]

		if (controlData) {
			const description = controlData.description
			this.setAttributeDescription('control', description)
		}
		return this
	}


	getAttributes(withModifiers: boolean = true) {
		const pendantHeight = this.getAttribute('pendantHeight', withModifiers)
		return {
			model: this.getAttribute('model', withModifiers),
			color: this.getAttribute('color', withModifiers),
			temperature: this.getAttribute('temperature', withModifiers),
			control: this.getAttribute('control', withModifiers),
			installation: this.getAttribute('installation', withModifiers),
			material: this.getAttribute('material', withModifiers),
			cri: this.getAttribute('cri', withModifiers),
			...pendantHeight && { pendantHeight }
		}
	}

	// Hacerlo un subset de la llamada de getAttributes
	getDomainAttributes(withModifiers: boolean) {
		const color = this.getAttribute('color', withModifiers)
		const temperature = this.getAttribute('temperature', withModifiers)
		const control = this.getAttribute('control', withModifiers)
		const installation = this.getAttribute('installation', withModifiers)
		const pendantHeight = this.getAttribute('pendantHeight', withModifiers)

		return {
			...color && { color },
			...temperature && { temperature },
			...control && { control },
			...installation && { installation },
			...pendantHeight && { pendantHeight }
		}
	}

	setHasAttributes(variationTypes?: variationType[], crmAttributes?: crmAttribute[]) {
		this.props.hasAttributes = crmAttributes
			? crmAttributes
			: variationTypes.map(attribute => Article.getAttributeKey(attribute))
	}

	// TODO: Check if still being used
	setPendantHeight(pendantHeight: number) {
		this.props.attributes.pendantHeight = {
			...this.props.attributes.pendantHeight ?? {},
			...UnitConverter.toObjectLengthByCatalog(
				this.getProps().catalog.id,
				Math.ceil(pendantHeight * 100),
				UnitConverter.UD_TYPES.CENTIMETERS
			)
		}
	}

	deleteAttribute(attribute: variationType) {
		const removableAttributes = ['control', 'installation', 'temperature']
		if (!removableAttributes.includes(attribute)) {
			return this
		}

		const attributeKey = Article.getAttributeKey(attribute)
		const newProps = JSON.parse(JSON.stringify(this.props))
		newProps.reference = newProps.model
		delete newProps.attributes[attributeKey]
		this.props = newProps
		return this
	}

	// Recibe atributo de composicion y te devuelve el equivalente del catalogo
	static getAttributeKey(variationKey?: variationType, crmKey?: crmAttribute): crmAttribute | variationType {
		for (const [variationValue, crmValue] of Object.entries(crmKeys)) {
			if (crmKey && crmKey == crmValue) {
				return variationValue as variationType
			} else if (variationKey && variationKey == variationValue) {
				return crmValue as crmAttribute
			}
		}
	}

}